/**
 * Utilitário unificado para CNPJ alfanumérico (IN RFB 2229/2024) e CPF.
 *
 * O novo CNPJ alfanumérico possui 14 caracteres:
 *  - 12 caracteres alfanuméricos no corpo (dígitos 0-9 e letras A-Z)
 *  - 2 dígitos verificadores numéricos (0-9)
 * Formato visual: XX.XXX.XXX/XXXX-DD
 */

/**
 * Converte um caractere alfanumérico para seu valor numérico no algoritmo do CNPJ.
 * '0'-'9' → 0-9  |  'A'-'Z' → 10-35
 */
function charToValue(char: string): number {
  const code = char.toUpperCase().charCodeAt(0);
  if (code >= 48 && code <= 57) return code - 48;  // '0'-'9'
  if (code >= 65 && code <= 90) return code - 55;  // 'A'-'Z' → 10-35
  return -1;
}

/**
 * Valida um CNPJ numérico ou alfanumérico (IN RFB 2229/2024).
 * Aceita tanto o formato com pontuação (XX.XXX.XXX/XXXX-DD) quanto sem.
 * Compatível com CNPJs numéricos legados.
 */
export function validarCNPJ(cnpj: string): boolean {
  const raw = cnpj.replace(/[.\-\/\s]/g, '').toUpperCase();

  if (raw.length !== 14) return false;

  // Todos os 14 caracteres devem ser alfanuméricos (A-Z ou 0-9)
  if (!/^[0-9A-Z]{14}$/.test(raw)) return false;

  // Rejeita sequências com todos os caracteres iguais
  if (/^(.)\1{13}$/.test(raw)) return false;

  const body = raw.slice(0, 12);
  const dvs  = raw.slice(12);

  // Os dois dígitos verificadores devem ser numéricos
  if (!/^\d{2}$/.test(dvs)) return false;

  const calcDV = (str: string, weights: number[]): number => {
    const sum = str.split('').reduce((acc, c, i) => acc + charToValue(c) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  if (calcDV(body, w1) !== parseInt(dvs[0], 10)) return false;
  if (calcDV(body + dvs[0], w2) !== parseInt(dvs[1], 10)) return false;

  return true;
}

/**
 * Valida um CPF (somente numérico).
 */
export function validarCPF(cpf: string): boolean {
  const raw = cpf.replace(/[.\-\s]/g, '');

  if (raw.length !== 11 || !/^\d{11}$/.test(raw)) return false;

  // Rejeita sequências com todos os dígitos iguais
  if (/^(\d)\1{10}$/.test(raw)) return false;

  let soma = 0;
  for (let i = 1; i <= 9; i++) soma += parseInt(raw[i - 1], 10) * (11 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(raw[9], 10)) return false;

  soma = 0;
  for (let i = 1; i <= 10; i++) soma += parseInt(raw[i - 1], 10) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(raw[10], 10)) return false;

  return true;
}

/**
 * Aplica a máscara de CNPJ alfanumérico em tempo real (XX.XXX.XXX/XXXX-DD).
 * Aceita letras e dígitos; remove qualquer outro caractere.
 */
export function mascaraCNPJ(v: string): string {
  const clean = v.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 14);

  if (clean.length <= 2)  return clean;
  if (clean.length <= 5)  return `${clean.slice(0, 2)}.${clean.slice(2)}`;
  if (clean.length <= 8)  return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5)}`;
  if (clean.length <= 12) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8)}`;
  return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12)}`;
}

/**
 * Aplica a máscara de CPF em tempo real (XXX.XXX.XXX-XX).
 */
export function mascaraCPF(v: string): string {
  const clean = v.replace(/\D/g, '').slice(0, 11);

  if (clean.length <= 3) return clean;
  if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
  if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
  return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9)}`;
}

/**
 * Aplica a máscara de CPF ou CNPJ conforme o conteúdo da entrada.
 * Aplica máscara de CNPJ quando o input contiver letras ou mais de 11 chars limpos.
 */
export function mascaraCpfCnpj(input: string): string {
  const clean = input.replace(/[^A-Z0-9]/gi, '');
  if (/[A-Z]/i.test(clean) || clean.length > 11) {
    return mascaraCNPJ(input);
  }
  return mascaraCPF(input);
}
